import { UUID } from '@/api/generatedApi';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { useQuery } from '@tanstack/react-query';

export const GET_DOCUMENT_PREVIEW_QUERY_KEY = 'partFinderReports.getDocumentPreview';

export type DocumentPreviewSize = 'full' | 'card';

export const useGetDocumentPreview = ({
  reportId,
  index,
  size = 'full',
  enabled = true,
}: {
  reportId: UUID;
  index: number;
  size?: DocumentPreviewSize;
  enabled?: boolean;
}) => {
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();
  const {
    data: preview,
    isLoading,
    error,
  } = useQuery({
    queryKey: [GET_DOCUMENT_PREVIEW_QUERY_KEY, { reportId, index, size }],
    queryFn: async () => {
      const { data, status, headers } = await api.http.request<Blob>({
        path: `/tenants/${tenantId}/part-finder/reports/${reportId}/documents/${index}-preview.png`,
        method: 'GET',
        query: { size },
        secure: true,
        format: 'blob',
      });

      if (status !== 200) {
        throw new Error('Failed to download document preview');
      }

      const contentType = headers['content-type'];
      return new Blob([data], { type: typeof contentType === 'string' ? contentType : undefined });
    },
    enabled: enabled && !!reportId && !!tenantId,
    refetchOnMount: false,
    retry: false,
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

  return { preview, isLoading, error };
};

const BLOB_URL_REVOKE_DELAY_MS = 60 * 1000;

export async function openHostedDocumentPdf(tenantId: UUID, reportId: UUID, index: number): Promise<void> {
  const pdfWindow = window.open('about:blank', '_blank');
  try {
    const { data, status, headers } = await api.http.request<Blob>({
      path: `/tenants/${tenantId}/part-finder/reports/${reportId}/documents/${index}.pdf`,
      method: 'GET',
      secure: true,
      format: 'blob',
    });
    if (status !== 200) {
      throw new Error('Failed to download hosted document PDF');
    }
    const contentType = headers['content-type'];
    const url = URL.createObjectURL(
      new Blob([data], { type: typeof contentType === 'string' ? contentType : 'application/pdf' })
    );
    setTimeout(() => URL.revokeObjectURL(url), BLOB_URL_REVOKE_DELAY_MS);
    if (pdfWindow) {
      pdfWindow.location.href = url;
    } else {
      window.open(url, '_blank');
    }
  } catch (error) {
    pdfWindow?.close();
    throw error;
  }
}
